import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/api';
import { API, formatError } from '../services/api';
import { motion } from 'framer-motion';
import { Spinner, EnvelopeSimple, ShieldCheck } from '@phosphor-icons/react';

/*
  Forgot Password — public page.
  Always shows success UI (we never leak whether the email exists).
*/
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center px-8 safe-top" data-testid="forgot-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <img src="/kindra-logo.png" alt="Kindra" className="h-20 mx-auto object-contain mb-8" />
          <div className="bg-white border border-[#235347]/10 rounded-2xl p-8">
            <div className="w-14 h-14 rounded-full bg-[#235347]/10 flex items-center justify-center mx-auto mb-5">
              <EnvelopeSimple size={24} className="text-[#235347]" weight="duotone" />
            </div>
            <h2 className="text-xl font-semibold text-[#0f1a17] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Check your inbox
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              If an account exists for <span className="text-slate-700 font-medium">{email}</span>, we've sent a reset link there. It's good for one hour.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed mb-6">
              Didn't get an email? Check your spam folder. If it's still not there, the address may not be registered.
            </p>
            <Link
              to="/login"
              className="inline-block text-[#235347] text-sm font-medium hover:underline underline-offset-2"
              data-testid="back-to-login-btn"
            >
              Back to sign in
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center px-8 safe-top">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src="/kindra-logo.png" alt="Kindra" className="h-24 mx-auto object-contain mb-6" />
          <h2 className="text-2xl font-light text-[#0f1a17]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="forgot-heading">
            Reset your password
          </h2>
          <p className="text-slate-500 text-sm mt-2 max-w-xs mx-auto leading-relaxed">
            Enter your email and we'll send you a link to set a new password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="forgot-error">{error}</div>
          )}
          <input
            data-testid="forgot-email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
            required
            autoFocus
          />
          <button
            data-testid="forgot-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#235347] text-white rounded-full px-6 py-4 font-medium text-base hover:bg-[#1c4439] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Spinner size={20} className="animate-spin" />}
            Send reset link
          </button>
          <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5 pt-1">
            <ShieldCheck size={12} /> Link expires in 1 hour · single-use
          </p>
        </form>

        <p className="text-center mt-8 text-slate-500 text-sm">
          Remembered it?{' '}
          <Link to="/login" className="text-[#235347] font-medium hover:underline underline-offset-2" data-testid="forgot-back-to-login">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
